// App.jsx — Canada-only (no address fields; keep only phone)
// Replace CANADIAN_PHONE with your real +1 number (E.164), e.g. +14165550123

import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { PopupProvider } from "./context/PopupContext";
import "bootstrap/dist/css/bootstrap.min.css";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { FaWhatsapp } from "react-icons/fa";
import { Helmet } from "react-helmet";

import {
  Testimonials,
  Contact,
  Footer,
  Process,
  Team,
  Services,
  Slider,
  Slider1,
  CTA,
  Partners,
  DynamicPage,
  Choose,
  HeroM,
  PricingTable
} from "./container";
import { Menu } from "./components";
import { gtagSendEvent } from "./utils/gtaghelper";
import TermsAndConditions from "./container/Terms";
import ContactPopup from "./container/Contactpopup/Contactpop";
import ContactFormPopup from "./container/ContactFormPopup/ContactFormPopup";
import PrivacyPolicy from "./container/privacy";
import RefundsAndCancellation from "./container/refunds";
import PaymentPage from "./container/pay";
import Success from "./container/sucess";

const SITE_URL = "https://ca.quantech.pro";

// Only phone is required — no address fields included.
const CANADIAN_PHONE = "+916363053425"; // TODO: replace with real +1 number

const App = () => (
  <PopupProvider>
  <Router>
    <div className="container">
      <Analytics />
      <SpeedInsights />
      <Menu />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/pay" element={<PaymentPage />} />
        <Route path="/status" element={<Success />} />

        <Route
          path="/website-development"
          element={
            <>
              <Helmet>
                <title>Website Development Services — Quantech (Canada)</title>

                <meta
                  name="description"
                  content="Quantech — website development & SEO for Canadian businesses. Contact us for CAD pricing & Canadian enquiries."
                />

                <link rel="canonical" href={`${SITE_URL}/website-development`} />
                <link rel="alternate" href={SITE_URL} hreflang="en-CA" />

                <meta property="og:locale" content="en_CA" />
                <meta property="og:title" content="Website Development — Quantech (Canada)" />
                <meta property="og:description" content="Fast, SEO-friendly websites for Canadian businesses. Contact for CAD quotes." />

                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Service",
                    name: "Website Development",
                    areaServed: "Canada",
                    provider: {
                      "@type": "LocalBusiness",
                      name: "Quantech",
                      telephone: CANADIAN_PHONE
                    },
                    offers: {
                      "@type": "Offer",
                      priceCurrency: "CAD",
                      price: "Call for pricing",
                      availability: "https://schema.org/InStock"
                    }
                  })}
                </script>
              </Helmet>

              <Marketing />
            </>
          }
        />

        <Route path="terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="refunds-and-cancellation" element={<RefundsAndCancellation />} />
        <Route path="/:slug" element={<DynamicPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <Footer />
    </div>
  </Router>
  </PopupProvider>
);

const NotFoundPage = () => {
  return <h1>404 Not Found</h1>;
};

const Marketing = () => {
  // Single Canada WhatsApp float (uses CANADIAN_PHONE)
  return (
    <>
      <HeroM />
      <Slider />
      <Partners />
      <CTA />
      <Testimonials />
      <Slider1 />
      <Contact />
      <ContactPopup />
      <ContactFormPopup />

      <div className="whatsapp-float" style={whatsappButtonStyle}>
        <a
          href={`https://api.whatsapp.com/send/?phone=${CANADIAN_PHONE.replace(/[^+\d]/g, "")}&text=Hi%2C%20I%20would%20like%20a%20quote%20for%20a%20website&type=phone_number&app_absent=0`}
          onClick={() =>
            gtagSendEvent(
              "conversion_event_contact_ca",
              `https://api.whatsapp.com/send/?phone=${CANADIAN_PHONE.replace(/[^+\d]/g, "")}`
            )
          }
          title="WhatsApp — Canada"
        >
          <FaWhatsapp style={iconStyle} />
        </a>
      </div>
    </>
  );
};

const Home = () => (
  <>
    <Helmet>
      <title>QuanTech | Website development for Canadian businesses</title>

      <meta
        name="description"
        content="Quantech — Fast, SEO-friendly websites built for Canadian businesses. Contact us for CAD pricing and Canadian support."
      />

      <link rel="canonical" href={SITE_URL} />
      <link rel="alternate" href={SITE_URL} hreflang="en-CA" />

      <meta property="og:locale" content="en_CA" />
      <meta property="og:title" content="QuanTech — Website development (Canada)" />
      <meta property="og:description" content="Professional website development and SEO services for Canadian startups and small businesses." />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": SITE_URL,
          name: "Quantech",
          url: SITE_URL,
          telephone: CANADIAN_PHONE,
          email: "hello@quantech.pro",
          priceRange: "Contact for CAD pricing",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "Customer Service",
            name: "QuanTech — Canada enquiries",
            telephone: CANADIAN_PHONE,
            email: "hello@quantech.pro",
            availableLanguage: ["English"]
          },
          areaServed: "Canada",
          makesOffer: {
            "@type": "Offer",
            priceCurrency: "CAD",
            price: "Call for pricing",
            availability: "https://schema.org/InStock",
            itemOffered: {
              "@type": "Service",
              name: "Website Development"
            }
          }
        })}
      </script>
    </Helmet>

    <HeroM />
    <Slider />
    <Services />
    <PricingTable />
    <Team />
    <CTA />
    <Partners />
    <Process />
    <Choose />
    <Testimonials />
    <Slider1 />
    <Contact />
    <ContactFormPopup />
  </>
);

const whatsappButtonStyle = {
  position: "fixed",
  bottom: "80px",
  right: "20px",
  backgroundColor: "#B9FF66",
  padding: "12px",
  borderRadius: "50%",
  zIndex: 1000,
};

const iconStyle = {
  color: "black",
  fontSize: "45px",
};

export default App;